import { useCallback, useState } from "react";
import { listRolesReq, createRoleReq, updateRoleReq, deleteRoleReq } from "services/api";
import { ROLES_MESSAGES } from "./roles.constants";

const parseErrorMessage = (err, fallbackMessage) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.message || data?.errors?.[0]?.msg || fallbackMessage;
};

function useRoles(adminId) {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!adminId) return;
    setIsLoading(true);
    try {
      const res = await listRolesReq({ admin_id: adminId });
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, "Failed to load roles."));
    }
    setIsLoading(false);
  }, [adminId]);

  // Runs a change, reports the result, and reloads the list. Returns true on success.
  const run = async (request, successText, failureText) => {
    try {
      setErrorMessage(null);
      setSuccessMessage(null);
      await request();
      setSuccessMessage(successText);
      await fetchAll();
      return true;
    } catch (err) {
      setErrorMessage(parseErrorMessage(err, failureText));
      return false;
    }
  };

  const createRole = (body) =>
    run(
      () => createRoleReq({ admin_id: adminId, body }),
      ROLES_MESSAGES.created,
      "Failed to create role."
    );

  const updateRole = (id, body) =>
    run(
      () => updateRoleReq({ admin_id: adminId, id, body }),
      ROLES_MESSAGES.updated,
      "Failed to update role."
    );

  const deleteRole = (id) =>
    run(
      () => deleteRoleReq({ admin_id: adminId, id }),
      ROLES_MESSAGES.deleted,
      "Failed to delete role."
    );

  return {
    roles,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createRole,
    updateRole,
    deleteRole,
    clearError: () => setErrorMessage(null),
    clearSuccess: () => setSuccessMessage(null),
  };
}

export default useRoles;
